import { useMemo } from "react";

import { ChatView } from "../ChatView/ChatView";
import type { AssistantPaymentCard } from "../PaymentCards/helpers/parsePaymentCards";

import { DONOR_ASSISTANT_CONFIG } from "./donorAssistantConfig";
import { DonorAssistantChat, useDonorAssistant } from "./useDonorAssistant";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { ASSISTANT_CONVERSATION_QUERY_OPTIONS } from "@/features/Assistant/helpers/conversationQueryOptions";
import { createConversationStorage } from "@/features/Assistant/helpers/conversationStorage";
import type { AssistantChatAdapter, AssistantChatControls } from "@/features/Assistant/helpers/types";

export interface DonorAssistantChatControls extends AssistantChatControls {
  askAboutPayment: (payment: AssistantPaymentCard) => void;
}

const DONOR_SESSION_STORAGE_KEY = "zeffy:donor-assistant:session";

const ignorePayment = () => undefined;

const useDonorScope = () => {
  const { currentUser } = useCurrentUserContext();
  const userId = currentUser?.id;
  return useMemo(
    () =>
      userId
        ? { key: userId, storage: createConversationStorage({ key: `${DONOR_SESSION_STORAGE_KEY}:${userId}` }) }
        : null,
    [userId]
  );
};

const useDonorConversation = (aiSessionId: string | null, { enabled }: { enabled: boolean }) =>
  trpc.getDonorAssistantConversation.useQuery(
    { aiSessionId: aiSessionId ?? "" },
    { ...ASSISTANT_CONVERSATION_QUERY_OPTIONS, enabled: enabled && Boolean(aiSessionId) }
  );

const useDonorFeedbackMutations = () => {
  const rate = trpc.rateDonorAssistantConversation.useMutation();
  const { mutateAsync: show } = trpc.showDonorAssistantFeedback.useMutation();
  return { rate, show };
};

export const DONOR_CHAT_ADAPTER: AssistantChatAdapter<DonorAssistantChat, DonorAssistantChatControls> = {
  config: DONOR_ASSISTANT_CONFIG,
  useScope: useDonorScope,
  useConversation: useDonorConversation,
  useAssistant: useDonorAssistant,
  useFeedbackMutations: useDonorFeedbackMutations,
  toControls: (base, chat) => ({ ...base, askAboutPayment: chat?.askAboutPayment ?? ignorePayment }),
  ChatView,
};
